"use client"

import * as React from "react"

import { createPersistedStore, usePersistedStore } from "@/lib/persisted-store"
import {
  DEFAULT_EXAM_CATEGORIES,
  EXAM_CATEGORIES_STORE_KEY,
  applyCanonicalDomainNames,
  buildCategoryTree,
  findCategory,
  getChildCategories,
  getDescendantCategoryIds,
  canMoveUnder,
  getRootCategories,
  mergeMissingDefaultCategories,
  categoriesForScenario,
  CLASSIFICATION_SCENARIO_STORE_KEY,
  type ClassificationScenario,
  type ExamCategory,
  type ExamCategoryTreeNode,
} from "@/lib/exam-categories"

/** One copy for the whole app, so a hidden tab can never write an old list over a new one. */
const categoriesStore = createPersistedStore<ExamCategory[]>(EXAM_CATEGORIES_STORE_KEY, () =>
  mergeMissingDefaultCategories(DEFAULT_EXAM_CATEGORIES.map((category) => ({ ...category }))),
)

/** Which demo scenario the Content tree is in (profile menu › Demo state). */
const scenarioStore = createPersistedStore<ClassificationScenario>(CLASSIFICATION_SCENARIO_STORE_KEY, () => "full")

const fullCatalog = () => mergeMissingDefaultCategories(DEFAULT_EXAM_CATEGORIES.map((category) => ({ ...category })))

export function useClassificationScenario() {
  const [scenario] = usePersistedStore(scenarioStore)
  /** Switching rebuilds the categories from the catalog, so edits made since are dropped. */
  const choose = React.useCallback((next: ClassificationScenario) => {
    scenarioStore.set(next)
    categoriesStore.set(categoriesForScenario(fullCatalog(), next))
  }, [])
  return [scenario, choose] as const
}

export function useExamCategories() {
  const [categories, setCategories] = usePersistedStore(categoriesStore)

  React.useEffect(() => {
    // A smaller demo scenario keeps the catalog from filling back in.
    if (scenarioStore.get() !== "full") return
    setCategories((current) => {
      const named = applyCanonicalDomainNames(current)
      const next = mergeMissingDefaultCategories(named)
      const changed =
        next.length !== current.length ||
        next.some((category, index) => category.name !== current[index]?.name || category.id !== current[index]?.id)
      return changed ? next : current
    })
  }, [setCategories])

  const tree = React.useMemo<ExamCategoryTreeNode[]>(
    () => buildCategoryTree(categories),
    [categories],
  )

  const roots = React.useMemo(() => getRootCategories(categories), [categories])

  const upsertCategory = React.useCallback((next: ExamCategory) => {
    setCategories((current) => {
      const index = current.findIndex((category) => category.id === next.id)
      if (index === -1) return [...current, next]
      const copy = [...current]
      copy[index] = next
      return copy
    })
  }, [setCategories])

  const addCategory = React.useCallback(
    (input: Omit<ExamCategory, "id" | "sortOrder"> & { id?: string }) => {
      const siblings = input.parentId
        ? getChildCategories(categories, input.parentId)
        : getRootCategories(categories)
      const next: ExamCategory = {
        id: input.id ?? `cat-${Date.now()}`,
        name: input.name.trim(),
        parentId: input.parentId,
        description: input.description?.trim() || undefined,
        // Only a root names its standard; a descendant takes its root's.
        ...(input.parentId ? {} : { dimensionId: input.dimensionId }),
        sortOrder: siblings.length,
        isActive: input.isActive ?? true,
      }
      upsertCategory(next)
      return next
    },
    [categories, upsertCategory],
  )

  /**
   * Several categories under one parent in a single write, in the order given.
   * `addCategory` in a loop would read the same snapshot for each, so they
   * would share an id (`cat-<ms>`) and a sort order.
   */
  const addCategories = React.useCallback(
    (
      parentId: string | null,
      items: readonly { name: string; description?: string; isActive?: boolean }[],
      dimensionId?: string,
    ): ExamCategory[] => {
      const stamp = Date.now().toString(36)
      const created: ExamCategory[] = []
      setCategories((current) => {
        const start = (parentId ? getChildCategories(current, parentId) : getRootCategories(current)).length
        items.forEach((item, index) => {
          created.push({
            id: `cat-${stamp}-${index}`,
            name: item.name.trim(),
            parentId,
            description: item.description?.trim() || undefined,
            ...(parentId ? {} : { dimensionId }),
            sortOrder: start + index,
            isActive: item.isActive ?? true,
          })
        })
        return [...current, ...created]
      })
      return created
    },
    [setCategories],
  )

  const updateCategory = React.useCallback(
    (categoryId: string, patch: Partial<Omit<ExamCategory, "id">>) => {
      setCategories((current) =>
        current.map((category) =>
          category.id === categoryId ? { ...category, ...patch } : category,
        ),
      )
    },
    [setCategories],
  )

  const removeCategory = React.useCallback(
    (categoryId: string) => {
      setCategories((current) => {
        const descendantIds = getDescendantCategoryIds(current, categoryId)
        const removeIds = new Set([categoryId, ...descendantIds])
        return current.filter((category) => !removeIds.has(category.id))
      })
    },
    [setCategories],
  )

  const moveCategory = React.useCallback(
    (categoryId: string, direction: "up" | "down") => {
      setCategories((current) => {
        const node = current.find((category) => category.id === categoryId)
        if (!node) return current
        // Siblings are the same parent's children; at the top they are the same
        // tree's roots, not every tree's.
        const treeOf = (category: ExamCategory) => category.dimensionId ?? "course-content"
        const siblings = current
          .filter(
            (category) =>
              category.parentId === node.parentId &&
              (node.parentId !== null || treeOf(category) === treeOf(node)),
          )
          .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name))
        const index = siblings.findIndex((category) => category.id === categoryId)
        const swapIndex = direction === "up" ? index - 1 : index + 1
        if (swapIndex < 0 || swapIndex >= siblings.length) return current
        // Renumber the whole row so equal or gapped sort orders cannot stall a move.
        const order = [...siblings]
        ;[order[index], order[swapIndex]] = [order[swapIndex]!, order[index]!]
        const position = new Map(order.map((category, i) => [category.id, i]))
        return current.map((category) =>
          position.has(category.id) ? { ...category, sortOrder: position.get(category.id)! } : category,
        )
      })
    },
    [setCategories],
  )

  /** Drag and drop: put a category where a sibling is (same parent, same tree). */
  const reorderCategory = React.useCallback(
    (dragId: string, targetId: string) => {
      setCategories((current) => {
        const drag = current.find((category) => category.id === dragId)
        const target = current.find((category) => category.id === targetId)
        if (!drag || !target || drag.parentId !== target.parentId || dragId === targetId) return current
        const treeOf = (category: ExamCategory) => category.dimensionId ?? "course-content"
        if (drag.parentId === null && treeOf(drag) !== treeOf(target)) return current
        const siblings = current
          .filter(
            (category) =>
              category.parentId === drag.parentId &&
              (drag.parentId !== null || treeOf(category) === treeOf(drag)),
          )
          .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name))
        const from = siblings.findIndex((category) => category.id === dragId)
        const to = siblings.findIndex((category) => category.id === targetId)
        const order = [...siblings]
        const [moving] = order.splice(from, 1)
        order.splice(to, 0, moving!)
        const position = new Map(order.map((category, i) => [category.id, i]))
        return current.map((category) =>
          position.has(category.id) ? { ...category, sortOrder: position.get(category.id)! } : category,
        )
      })
    },
    [setCategories],
  )

  const resetToDefaults = React.useCallback(() => {
    setCategories(DEFAULT_EXAM_CATEGORIES.map((category) => ({ ...category })))
  }, [setCategories])

  const moveCategoryBefore = React.useCallback(
    (activeId: string, overId: string) => {
      setCategories((current) => {
        const active = findCategory(current, activeId)
        const over = findCategory(current, overId)
        if (!active || !over || activeId === overId) return current
        const descendants = getDescendantCategoryIds(current, activeId)
        if (descendants.includes(overId)) return current

        const targetParentId = over.parentId
        const nextSiblings = current
          .filter(
            (category) =>
              category.parentId === targetParentId && category.id !== activeId,
          )
          .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name))
        const insertIndex = nextSiblings.findIndex((category) => category.id === overId)
        if (insertIndex < 0) return current

        nextSiblings.splice(insertIndex, 0, { ...active, parentId: targetParentId })
        const sortOrderMap = new Map(
          nextSiblings.map((category, index) => [category.id, index]),
        )

        const oldParentId = active.parentId
        const oldSiblings = current
          .filter(
            (category) =>
              category.parentId === oldParentId && category.id !== activeId,
          )
          .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name))

        return current.map((category) => {
          if (sortOrderMap.has(category.id)) {
            return {
              ...category,
              parentId: targetParentId,
              sortOrder: sortOrderMap.get(category.id)!,
            }
          }
          if (category.parentId === oldParentId && category.id !== activeId) {
            const index = oldSiblings.findIndex((item) => item.id === category.id)
            if (index >= 0) return { ...category, sortOrder: index }
          }
          return category
        })
      })
    },
    [setCategories],
  )

  const reparentCategory = React.useCallback(
    (categoryId: string, newParentId: string | null, dimensionId?: string) => {
      setCategories((current) => {
        const active = findCategory(current, categoryId)
        if (!active || categoryId === newParentId) return current
        // Never past five levels, never under itself.
        if (!canMoveUnder(current, categoryId, newParentId)) return current

        const oldParentId = active.parentId
        const oldSiblings = getChildCategories(current, oldParentId).filter(
          (category) => category.id !== categoryId,
        )
        const nextOrder = getChildCategories(current, newParentId).filter(
          (category) => category.id !== categoryId,
        ).length

        return current.map((category) => {
          if (category.id === categoryId) {
            // A top-level category names its tree; one with a parent takes its parent's.
            const { dimensionId: _drop, ...rest } = category
            return {
              ...rest,
              parentId: newParentId,
              sortOrder: nextOrder,
              ...(newParentId === null && dimensionId ? { dimensionId } : {}),
            }
          }
          if (category.parentId === oldParentId && category.id !== categoryId) {
            const index = oldSiblings.findIndex((item) => item.id === category.id)
            if (index >= 0) return { ...category, sortOrder: index }
          }
          return category
        })
      })
    },
    [setCategories],
  )

  const replaceCategories = React.useCallback(
    (next: ExamCategory[]) => {
      setCategories(next.map((category) => ({ ...category })))
    },
    [setCategories],
  )

  return {
    categories,
    tree,
    roots,
    upsertCategory,
    addCategory,
    addCategories,
    updateCategory,
    removeCategory,
    moveCategory,
    reorderCategory,
    moveCategoryBefore,
    reparentCategory,
    resetToDefaults,
    replaceCategories,
  }
}
